// =============================================================
// VARIANCE — The Executive Command Center
// File      : VendorList.tsx
// Location  : /home/Applications/variance/web/src/pages/VendorList.tsx
// Owner     : VARIANCE frontend application only
// Purpose   : Premium list view for specific vendor types.
// =============================================================

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Plus, X, ArrowLeft } from 'lucide-react';

interface Company { id: string; name: string; }
interface Vendor {
  id: string;
  name: string;
  vendor_type: string;
  goods_services: string | null;
  address: string | null;
  email: string | null;
  phone1: string | null;
  phone2: string | null;
  representative: string | null;
  designation: string | null;
}

export function VendorList() {
  const { type } = useParams();
  const navigate = useNavigate();
  
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const [formData, setFormData] = useState({
    company_id: '',
    name: '',
    vendor_type: type || 'Contractor',
    goods_services: '',
    address: '',
    email: '',
    phone1: '',
    phone2: '',
    representative: '',
    designation: ''
  });
  const [formError, setFormError] = useState('');

  const fetchVendors = () => {
    setIsLoading(true);
    fetch('/api/vendors')
      .then(res => res.json())
      .then(data => { 
        if (!data.error) {
          // Filter based on URL parameter
          const filtered = data.filter((v: Vendor) => v.vendor_type.toLowerCase() === type?.toLowerCase());
          setVendors(filtered); 
        }
        setIsLoading(false); 
      })
      .catch(() => setIsLoading(false));
  };

  const fetchCompanies = () => {
    fetch('/api/companies')
      .then(res => res.json())
      .then(data => {
        if (!data.error && data.length > 0) {
          setCompanies(data);
          setFormData(prev => ({ ...prev, company_id: data[0].id }));
        }
      });
  };

  useEffect(() => {
    fetchVendors();
    fetchCompanies();
  }, [type]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    try {
      const res = await fetch('/api/vendors', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create vendor');
      
      setIsModalOpen(false);
      setFormData({ company_id: companies[0]?.id || '', name: '', vendor_type: type || 'Contractor', goods_services: '', address: '', email: '', phone1: '', phone2: '', representative: '', designation: '' });
      fetchVendors();
    } catch (err: any) {
      setFormError(err.message);
    }
  };

  return (
    <div className="space-y-6 relative min-h-screen">
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/vendors')} className="p-2 rounded-full hover:bg-[var(--bg-panel-alt)] transition-colors" style={{ color: 'var(--text-secondary)' }}>
            <ArrowLeft size={24} />
          </button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight capitalize" style={{ color: 'var(--text-primary)' }}>{type}s</h1>
            <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>Manage your {type?.toLowerCase()} network.</p>
          </div>
        </div>
        <button onClick={() => setIsModalOpen(true)} className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-white hover:opacity-90 transition-all shadow-[0_0_15px_var(--brand-accent)]" style={{ backgroundColor: 'var(--brand-accent)' }}>
          <Plus size={18} /> Add {type}
        </button>
      </div>

      {/* KPI Header */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-4 rounded-xl" style={{ backgroundColor: 'var(--bg-panel)', border: '1px solid var(--border-subtle)' }}>
          <div className="text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Total {type}s</div>
          <div className="text-3xl font-bold font-mono" style={{ color: 'var(--text-primary)' }}>{vendors.length}</div>
        </div>
        <div className="p-4 rounded-xl" style={{ backgroundColor: 'var(--bg-panel)', border: '1px solid var(--border-subtle)' }}>
          <div className="text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Active Work Orders</div>
          <div className="text-3xl font-bold font-mono" style={{ color: 'var(--text-primary)' }}>0</div>
          <div className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Coming soon</div>
        </div>
        <div className="p-4 rounded-xl" style={{ backgroundColor: 'var(--bg-panel)', border: '1px solid var(--border-subtle)' }}>
          <div className="text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Total Contracted Value</div>
          <div className="text-3xl font-bold font-mono" style={{ color: 'var(--text-primary)' }}>₹0</div>
          <div className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Coming soon</div>
        </div>
      </div>

      {/* Dense Data Table */}
      <div className="rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--bg-panel)', border: '1px solid var(--border-subtle)' }}>
        <table className="w-full text-left border-collapse">
          <thead>
            <tr style={{ backgroundColor: 'var(--bg-panel-alt)' }}>
              <th className="p-4 text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
                {type === 'Consultant' ? 'Consultant / SME' : type === 'Contractor' ? 'Contractor Name' : 'Supplier Name'}
              </th>
              <th className="p-4 text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
                {type === 'Consultant' ? 'Subject Matter Expertise' : type === 'Contractor' ? 'Scope of Work' : 'Materials Supplied'}
              </th>
              <th className="p-4 text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>Representative</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={4} className="p-8 text-center text-gray-400">Loading...</td></tr>
            ) : vendors.length === 0 ? (
              <tr><td colSpan={4} className="p-8 text-center text-gray-400">No {type?.toLowerCase()}s found. Add one to get started.</td></tr>
            ) : (
              vendors.map(v => (
                <tr key={v.id} className="border-t hover:bg-[var(--bg-panel-alt)] transition-colors cursor-pointer" style={{ borderColor: 'var(--border-subtle)' }} onClick={() => setSelectedVendor(v)}>
                  <td className="p-4 font-medium" style={{ color: 'var(--text-primary)' }}>{v.name}</td>
                  <td className="p-4 text-sm" style={{ color: 'var(--text-secondary)' }}>{v.goods_services || '-'}</td>
                  <td className="p-4 text-sm" style={{ color: 'var(--text-secondary)' }}>
                    {v.representative ? `${v.representative} (${v.phone1})` : '-'}
                  </td>
                  <td className="p-4 text-center">
                    <button className="px-3 py-1 text-xs font-medium rounded bg-[var(--bg-panel-alt)] hover:bg-[var(--brand-accent)] text-[var(--text-primary)] hover:text-white transition-colors">
                      View
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* SLIDE-OUT DETAILS PANEL */}
      {selectedVendor && (
        <div className="fixed inset-0 z-40 flex justify-end bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="w-full max-w-md h-full flex flex-col shadow-2xl animate-slide-in-right" style={{ backgroundColor: 'var(--bg-panel)', borderLeft: '1px solid var(--border-subtle)' }}>
            <div className="p-6 border-b flex justify-between items-start" style={{ borderColor: 'var(--border-subtle)' }}>
              <div>
                <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{selectedVendor.name}</h2>
                <div className="text-sm mt-1" style={{ color: 'var(--brand-accent)' }}>{selectedVendor.vendor_type}</div>
              </div>
              <button onClick={() => setSelectedVendor(null)} className="p-2 rounded-full hover:bg-[var(--bg-panel-alt)] transition-colors" style={{ color: 'var(--text-secondary)' }}>
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-8">
              <div>
                <h3 className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: 'var(--brand-accent)' }}>Contact Details</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between py-1 border-b" style={{ borderColor: 'var(--border-subtle)' }}>
                    <span style={{ color: 'var(--text-secondary)' }}>Representative</span>
                    <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{selectedVendor.representative || '-'}</span>
                  </div>
                  <div className="flex justify-between py-1 border-b" style={{ borderColor: 'var(--border-subtle)' }}>
                    <span style={{ color: 'var(--text-secondary)' }}>Designation</span>
                    <span style={{ color: 'var(--text-primary)' }}>{selectedVendor.designation || '-'}</span>
                  </div>
                  <div className="flex justify-between py-1 border-b" style={{ borderColor: 'var(--border-subtle)' }}>
                    <span style={{ color: 'var(--text-secondary)' }}>Phone 1</span>
                    <span className="font-mono" style={{ color: 'var(--text-primary)' }}>{selectedVendor.phone1 || '-'}</span>
                  </div>
                  <div className="flex justify-between py-1 border-b" style={{ borderColor: 'var(--border-subtle)' }}>
                    <span style={{ color: 'var(--text-secondary)' }}>Phone 2</span>
                    <span className="font-mono" style={{ color: 'var(--text-primary)' }}>{selectedVendor.phone2 || '-'}</span>
                  </div>
                  <div className="flex justify-between py-1 border-b" style={{ borderColor: 'var(--border-subtle)' }}>
                    <span style={{ color: 'var(--text-secondary)' }}>Email</span>
                    <span style={{ color: 'var(--text-primary)' }}>{selectedVendor.email || '-'}</span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: 'var(--brand-accent)' }}>Goods / Services</h3>
                <div className="p-3 rounded-lg bg-[var(--bg-panel-alt)] text-sm" style={{ color: 'var(--text-primary)' }}>
                  {selectedVendor.goods_services || 'No description provided.'}
                </div>
              </div>

              <div>
                <h3 className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: 'var(--brand-accent)' }}>Address</h3>
                <div className="p-3 rounded-lg bg-[var(--bg-panel-alt)] text-sm" style={{ color: 'var(--text-primary)' }}>
                  {selectedVendor.address || 'No address provided.'}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ADD VENDOR MODAL */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm overflow-y-auto py-10">
          <div className="w-full max-w-2xl p-6 rounded-xl shadow-2xl" style={{ backgroundColor: 'var(--bg-panel)', border: '1px solid var(--border-subtle)' }}>
            <h2 className="text-xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>Add New {type}</h2>
            {formError && <div className="p-3 mb-4 rounded text-sm text-red-500 bg-red-500/10">{formError}</div>}
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>Company</label>
                  <select required value={formData.company_id} onChange={e => setFormData({...formData, company_id: e.target.value})} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-[var(--text-primary)]">
                    {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>Vendor Name</label>
                  <input required type="text" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-[var(--text-primary)]" />
                </div>
              </div>

              <div>
                <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>Details of Goods / Services</label>
                <input type="text" value={formData.goods_services} onChange={e => setFormData({...formData, goods_services: e.target.value})} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-[var(--text-primary)]" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>Representative</label>
                  <input type="text" value={formData.representative} onChange={e => setFormData({...formData, representative: e.target.value})} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-[var(--text-primary)]" />
                </div>
                <div>
                  <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>Designation</label>
                  <input type="text" value={formData.designation} onChange={e => setFormData({...formData, designation: e.target.value})} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-[var(--text-primary)]" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>Phone 1</label>
                  <input type="text" value={formData.phone1} onChange={e => setFormData({...formData, phone1: e.target.value})} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-[var(--text-primary)]" />
                </div>
                <div>
                  <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>Phone 2</label>
                  <input type="text" value={formData.phone2} onChange={e => setFormData({...formData, phone2: e.target.value})} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-[var(--text-primary)]" />
                </div>
              </div>

              <div className="flex gap-3 pt-6">
                <button type="button" onClick={() => setIsModalOpen(false)} className="flex-1 py-2 rounded font-medium bg-[var(--bg-panel-alt)] text-[var(--text-primary)]">Cancel</button>
                <button type="submit" className="flex-1 py-2 rounded font-medium text-white" style={{ backgroundColor: 'var(--brand-accent)' }}>Save Vendor</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
